import {
  getStoredSubdomain,
  setStoredSubdomain,
} from "./clinicStorage";
import { setAccessToken } from "./authStorage";

export const PORTAL_TOKEN_HASH_KEY = "portal_token";

const RESERVED = new Set([
  "api",
  "www",
  "app",
  "admin",
  "localhost",
]);

const BASE_DOMAIN = (process.env.REACT_APP_BASE_DOMAIN || "").trim().toLowerCase();
const PLATFORM_HOSTS = (process.env.REACT_APP_PLATFORM_HOSTS || "localhost,app")
  .split(",")
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);
const DEV_PORT = process.env.REACT_APP_DEV_PORT || "3000";

function parseHostname(hostname) {
  const host = String(hostname || "").split(":")[0].trim().toLowerCase();
  if (!host || host === "localhost" || host === "127.0.0.1") {
    return { subdomain: null, isPlatform: true };
  }
  if (host.endsWith(".localhost")) {
    const sub = host.replace(/\.localhost$/, "").split(".")[0];
    if (!sub || RESERVED.has(sub)) return { subdomain: null, isPlatform: true };
    return { subdomain: sub, isPlatform: false };
  }
  if (BASE_DOMAIN) {
    if (
      host === BASE_DOMAIN ||
      PLATFORM_HOSTS.some((p) => host === p || host === `${p}.${BASE_DOMAIN}`)
    ) {
      return { subdomain: null, isPlatform: true };
    }
    const suffix = `.${BASE_DOMAIN}`;
    if (host.endsWith(suffix)) {
      const prefix = host.slice(0, -suffix.length);
      const sub = prefix.includes(".") ? prefix.split(".")[0] : prefix;
      if (!sub || RESERVED.has(sub)) return { subdomain: null, isPlatform: true };
      return { subdomain: sub, isPlatform: false };
    }
  }
  const parts = host.split(".");
  if (parts.length >= 3) {
    const sub = parts[0];
    if (!RESERVED.has(sub)) return { subdomain: sub, isPlatform: false };
  }
  return { subdomain: null, isPlatform: true };
}

/** Поддомен из ?subdomain= — для localhost-ссылок регистрации. */
export function getSubdomainFromQuery() {
  if (typeof window === "undefined") return null;
  try {
    const raw = new URLSearchParams(window.location.search).get("subdomain");
    const sub = String(raw || "")
      .trim()
      .toLowerCase();
    if (!sub || RESERVED.has(sub)) return null;
    return sub;
  } catch {
    return null;
  }
}

export function getSubdomainFromHost() {
  if (typeof window === "undefined") return null;
  return (
    parseHostname(window.location.hostname).subdomain || getSubdomainFromQuery()
  );
}

export function isClinicPortal() {
  return Boolean(getSubdomainFromHost());
}

export function isPlatformHost() {
  return !isClinicPortal();
}

/** Поддомен для API: Host клуба. Sticky localStorage — только localhost без поддомена. */
export function getSubdomainForApi() {
  const fromHost = getSubdomainFromHost();
  if (fromHost) return fromHost;
  if (typeof window === "undefined") return getStoredSubdomain() || null;
  const host = String(window.location.hostname || "")
    .split(":")[0]
    .toLowerCase();
  // app.bay-sport.ru / bay-sport.ru — не тащим прошлый клуб в X-Tenant
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost")) {
    return getStoredSubdomain() || null;
  }
  return null;
}

/** Поддомен для форм входа на основном сайте */
export function getSubdomainForForms() {
  return getSubdomainFromHost() || getStoredSubdomain() || null;
}

export function buildClinicPortalUrl(subdomain, path = "/") {
  const sub = String(subdomain || "").trim().toLowerCase();
  if (!sub) return path;
  const p = path.startsWith("/") ? path : `/${path}`;
  const proto = window.location.protocol;
  const port = window.location.port;

  if (BASE_DOMAIN) {
    const host = `${sub}.${BASE_DOMAIN}`;
    const portPart =
      port && port !== "80" && port !== "443" ? `:${port}` : "";
    return `${proto}//${host}${portPart}${p}`;
  }

  const host = `${sub}.localhost`;
  const portPart = port ? `:${port}` : `:${DEV_PORT}`;
  return `${proto}//${host}${portPart}${p}`;
}

/** Считать access-токен из hash после перехода с платформы (до инициализации auth). */
export function consumePortalTokenFromHash() {
  if (typeof window === "undefined") return false;
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw) return false;
  const token = new URLSearchParams(raw).get(PORTAL_TOKEN_HASH_KEY);
  if (!token) return false;
  setAccessToken(token);
  window.history.replaceState(
    null,
    "",
    window.location.pathname + window.location.search,
  );
  return true;
}

export function redirectToClinicPortal(subdomain, path = "/", accessToken) {
  const sub = String(subdomain || "").trim().toLowerCase();
  setStoredSubdomain(sub);
  const url = buildClinicPortalUrl(sub, path);
  const token = accessToken || null;
  const target =
    token && url.startsWith("http")
      ? `${url}#${PORTAL_TOKEN_HASH_KEY}=${encodeURIComponent(token)}`
      : url;
  if (target.startsWith("http")) {
    window.location.href = target;
  } else {
    window.location.pathname = path;
  }
}

export function getPlatformUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (BASE_DOMAIN) {
    const appHost =
      PLATFORM_HOSTS.find((h) => h !== "localhost") || `app.${BASE_DOMAIN}`;
    const host = appHost.includes(".") ? appHost : `${appHost}.${BASE_DOMAIN}`;
    return `${window.location.protocol}//${host}${p}`;
  }
  return `http://localhost:${DEV_PORT}${p}`;
}

/** Человекочитаемый хост клуба: `demo.bay-sport.ru` */
export function formatClinicHost(subdomain) {
  const sub = String(subdomain || "").trim().toLowerCase();
  if (!sub) return "";
  if (BASE_DOMAIN) return `${sub}.${BASE_DOMAIN}`;
  return `${sub}.localhost`;
}
